import {
  computed,
  getCurrentInstance,
} from 'vue';

import type { VNodeData } from 'vue';

type EmitFn = (event: string, ...args: unknown[]) => void;

interface ClickableProps {
  disabled?: boolean;
  ripple?: boolean | object | null;
  tag?: string;
}

interface UseClickableOptions {
  emit?: EmitFn;
}

export const clickableProps = {
  disabled: Boolean,
  ripple: {
    type: [Boolean, Object],
    default: null,
  },
};

export function useClickable (props: ClickableProps, { emit }: UseClickableOptions = {}) {
  const instance = getCurrentInstance();
  const vm = () => instance?.proxy;

  const isClickable = computed(() => {
    if (props.disabled) {
      return false;
    }

    const proxy = vm();

    return Boolean(proxy?.$listeners.click
      || proxy?.$listeners['!click']
      || proxy?.$attrs.tabindex);
  });

  const computedRipple = computed(() => props.ripple ?? (!props.disabled && isClickable.value));

  function click (event: Event) {
    emit?.('click', event);
  }

  function generateClickable () {
    const proxy = vm();
    const listeners = proxy?.$listeners || {};
    const attrs = proxy?.$attrs || {};

    const data: VNodeData = {
      attrs: {
        tabindex: 'tabindex' in attrs ? attrs.tabindex : false,
      },
      class: {},
      style: {},
      on: {
        ...listeners,
        click,
      },
      directives: [{
        name: 'ripple',
        value: computedRipple.value,
      }],
    };

    return {
      tag: props.tag || 'div',
      data,
    };
  }

  return {
    isClickable,
    computedRipple,
    click,
    generateClickable,
  };
}
